import type { FeatureKey } from '../schemas/entitlements/feature-key.schema'
import type { PlanTier } from '../schemas/entitlements/plan-tier.schema'
import type { ThemeKey } from '../schemas/entitlements/theme-key.schema'
import type { FeatureCategory } from './entitlements.constants'

/**
 * The `data-testid` vocabulary for the OC-320 entitlements surfaces.
 *
 * One definition, three consumers: the shared feature-gate components, the Core Manage Plan
 * page, and the Playwright specs. A rename here moves the markup and the specs together
 * instead of silently unhooking them.
 *
 * Ids are either a fixed name (one per page/panel/modal) or a builder parameterised by the
 * domain value it identifies — tier, feature key, feature category, theme, site id — so a spec
 * can address one tier or one feature by name without a lookup table.
 *
 * Where the meaning of an element is carried by an icon or a translated label, the element also
 * gets one of the data attributes below, so assertions never depend on locale or iconography.
 */

/** Fixed ids: at most one of each renders at a time. */
export const ENTITLEMENT_TESTIDS = {
  /** Route-level "your plan does not include this" screen. */
  featureDisabled: 'feature-disabled',
  planAndAddonsPage: 'plan-and-addons-page',
  planCardGrid: 'plan-card-grid',
  compareAllPlansLink: 'plan-and-addons-compare-all',
  comparePlansPage: 'compare-plans-page',
  comparePlansTable: 'compare-plans-table',
  comparePlansConfirm: 'compare-plans-confirm',
  managePlanPanel: 'manage-plan-panel',
  managePlanAllowOverride: 'manage-plan-allow-override',
  manageAddonsPanel: 'manage-addons-panel',
  locationPicker: 'location-picker',
  changePlanModal: 'change-plan-modal',
  cancelPlanModal: 'cancel-plan-modal',
  confirmAddonChangesModal: 'confirm-addon-changes-modal',
  confirmAddonToggle: 'confirm-addon-toggle',
} as const

/** Attribute names carrying state a spec would otherwise have to read off an icon or a label. */
export const ENTITLEMENT_TESTID_ATTRIBUTES = {
  /** Plan card: where this tier sits relative to the brand's current tier. */
  planState: 'data-plan-state',
  /** Plan card badge: which badge is showing. */
  badge: 'data-badge',
  /** Comparison cell: whether the tier includes the feature. */
  included: 'data-included',
} as const

export const PLAN_CARD_STATES = {
  current: 'current',
  upgrade: 'upgrade',
  downgrade: 'downgrade',
} as const
export type PlanCardState = (typeof PLAN_CARD_STATES)[keyof typeof PLAN_CARD_STATES]

export const PLAN_CARD_BADGES = {
  current: 'current',
  popular: 'popular',
  upgrade: 'upgrade',
  lowerTier: 'lower-tier',
} as const
export type PlanCardBadgeKind = (typeof PLAN_CARD_BADGES)[keyof typeof PLAN_CARD_BADGES]

/** Greyed-out region wrapped by `FeatureGate` when the location lacks the feature. */
export function buildFeatureGateTestId(feature: FeatureKey): string {
  return `feature-gate-${feature}`
}

/** The lock badge `FeatureGate` overlays on a gated region; hovering it shows the upgrade reason. */
export function buildFeatureGateLockTestId(feature: FeatureKey): string {
  return `feature-gate-${feature}-lock`
}

export function buildPlanCardTestId(tier: PlanTier): string {
  return `plan-card-${tier}`
}

export function buildPlanCardBadgeTestId(tier: PlanTier): string {
  return `plan-card-badge-${tier}`
}

export function buildPlanCardPriceTestId(tier: PlanTier): string {
  return `plan-card-price-${tier}`
}

export function buildPlanCardSelectTestId(tier: PlanTier): string {
  return `plan-card-select-${tier}`
}

export function buildPlanCardMenuTestId(tier: PlanTier): string {
  return `plan-card-menu-${tier}`
}

export function buildPlanCardAddonToggleTestId(theme: ThemeKey): string {
  return `plan-card-addon-toggle-${theme}`
}

export function buildComparePlanColumnTestId(tier: PlanTier): string {
  return `compare-column-${tier}`
}

export function buildCompareCategoryTestId(category: FeatureCategory): string {
  return `compare-category-${category}`
}

export function buildCompareRowTestId(feature: FeatureKey): string {
  return `compare-row-${feature}`
}

export function buildCompareCellTestId(feature: FeatureKey, tier: PlanTier): string {
  return `compare-cell-${feature}-${tier}`
}

export function buildAddonCardTestId(theme: ThemeKey): string {
  return `addon-card-${theme}`
}

export function buildAddonToggleTestId(theme: ThemeKey): string {
  return `addon-toggle-${theme}`
}

export function buildAddonOverrideToggleTestId(theme: ThemeKey): string {
  return `addon-override-toggle-${theme}`
}

export function buildLocationOptionTestId(siteId: string): string {
  return `location-option-${siteId}`
}
